<template>
  <div class="sell-order-details">
    <div v-if="sellDetails">
      <div class="row g-2 g-lg-3 align-items-stretch">
        <div class="col-xl-4">
          <div class="details-panel details-panel-summary h-100">
            <div class="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon="chart-line" class="text-warning me-2" />
              <span class="text-uppercase text-muted fw-semibold section-label">
                {{ $t("company_orders.sell.sale_summary") }}
              </span>
            </div>

            <div class="summary-content">
              <div class="text-muted small mb-1">
                {{ $t("company_orders.total_mwh") }}
              </div>
              <div class="fs-2 fw-semibold lh-sm">
                {{ totalSelectedMwh }}
                <span class="fs-6 text-muted fw-normal">MWh</span>
              </div>

              <div class="summary-detail">
                <div
                  class="text-muted small text-uppercase summary-detail-label"
                >
                  {{ $t("dashboard.portfolio.selected_certificates") }}
                </div>
                <div class="summary-detail-value">
                  {{ selectedPortfolioItemsCount }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="details-panel h-100">
            <div class="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon="cogs" class="text-info me-2" />
              <span class="text-uppercase text-muted fw-semibold section-label">
                {{ $t("company_orders.sell.process_information") }}
              </span>
            </div>

            <p class="text-muted small mb-3">
              {{ $t(descriptionKey, { minimumPrice }) }}
            </p>

            <div class="row g-2">
              <div class="col-md-6">
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.sell.source_portfolio") }}
                  </div>
                  <span class="badge bg-secondary rounded-pill">
                    {{ $t("charts.values.portfolio_type.HOLD") }}
                  </span>
                </div>
              </div>

              <div class="col-md-6">
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.sell.destination_portfolio") }}
                  </div>
                  <span class="badge bg-primary rounded-pill">
                    {{
                      $t(
                        `charts.values.portfolio_type.${destinationPortfolioKey}`
                      )
                    }}
                  </span>
                </div>
              </div>

              <div class="col-md-6">
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("company_orders.sell.operation_type") }}
                  </div>
                  <div class="fw-semibold">
                    {{ $t("company_orders.sell.internal_portfolio_transfer") }}
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ $t("dashboard.portfolio.selected_certificates") }}
                  </div>
                  <div class="fw-semibold">
                    {{ selectedPortfolioItemsCount }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fallback to generic display -->
    <GenericOrderDetailsSection v-else :details="details" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { SellOrderDetails } from "../../common/companyOrderTypes";
import GenericOrderDetailsSection from "./GenericOrderDetailsSection.vue";

interface Props {
  details: SellOrderDetails;
  totalSelectedMwh: number;
}

const props = defineProps<Props>();

const sellDetails = computed((): SellOrderDetails | null => {
  return props.details;
});

const selectedPortfolioItemsCount = computed((): number => {
  return sellDetails.value?.selected_portfolio_items.length ?? 0;
});

const minimumPrice = computed((): string | null => {
  const strategy = sellDetails.value?.strategy;
  if (strategy?.type !== "minimum") return null;
  const price = strategy.minimumPrice;
  return typeof price === "number" && Number.isFinite(price)
    ? price.toFixed(2)
    : null;
});

const isMinimumStrategy = computed((): boolean => minimumPrice.value !== null);

const destinationPortfolioKey = computed(
  (): "SPOT_MIN_PRICE" | "SPOT_MARKET_PRICE" =>
    isMinimumStrategy.value ? "SPOT_MIN_PRICE" : "SPOT_MARKET_PRICE"
);

const descriptionKey = computed((): string =>
  isMinimumStrategy.value
    ? "company_orders.sell.hold_to_min_price_description"
    : "company_orders.sell.hold_to_spot_description"
);
</script>

<style scoped lang="scss">
@use "../order-details-shared";

.details-panel {
  height: 100%;
}

.details-panel-summary {
  background: var(--phoenix-warning-bg-subtle);
  border-color: var(--phoenix-warning-border-subtle);
}

.summary-detail-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--phoenix-warning-text-emphasis);
}
</style>
